import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import TalentSelectorModal from '../components/TalentSelectorModal';
import ResidenceInfoModal from '../components/ResidenceInfoModal';
import LoanInfoModal from '../components/LoanInfoModal';
import AccountingAnalytics from '../components/AccountingAnalytics';
import SystemNotificationPanel from '../components/SystemNotificationPanel';
import SpecifiedSkillNewsPanel, { NewsActionCard } from '../components/SpecifiedSkillNewsPanel';
import FilterPanel from '../components/FilterPanel';
import TalentTable from '../components/TalentTable';
import { apiService } from '../services/apiService';
import { useTranslation } from 'react-i18next';
import { useTalentSearch } from '../hooks/useTalentSearch';
import { getInitialFilters, getFilterConfigByRole } from '../config/filterConfig';

export default function SharedDashboard() {
    const { user } = useAuth();
    const { t } = useTranslation();
    const isResidence = user.role === 'Residence';
    const isAccounting = user.role === 'Accounting';

    const [isSelectorOpen, setIsSelectorOpen] = useState(false);
    const [selectedTalent, setSelectedTalent] = useState(null);
    const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
    const [showBackButton, setShowBackButton] = useState(false);
    const [isHome, setIsHome] = useState(true);
    const [residenceView, setResidenceView] = useState('home');
    const isResidenceListActive = isResidence && residenceView === 'list';

    const {
        talents,
        isLoading,
        searchTerm,
        setSearchTerm,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        fetchTalents,
        currentPage,
        setCurrentPage,
        totalPages,
        totalCount,
        feeTotals,
        pageSize,
        filters,
        setFilters,
    } = useTalentSearch({ enabled: isResidenceListActive });

    useEffect(() => {
        if (!isResidence) return;

        const config = getFilterConfigByRole(user.role);
        setFilters(getInitialFilters(config));
    }, [isResidence, setFilters, user.role]);

    const handleTalentSelected = async (talentId) => {
        setIsSelectorOpen(false);
        try {
            const talentDetails = await apiService.getTalentById(talentId);
            setSelectedTalent(talentDetails);
            setIsDetailModalOpen(true);
            setShowBackButton(!isResidence);
        } catch (error) {
            alert(t('fetchFailed'));
        }
    };

    const handleCloseDetailModal = () => {
        setIsDetailModalOpen(false);
        setSelectedTalent(null);

        if (!isResidence) {
            setShowBackButton(false);
            setIsHome(true);
        }
    };

    const handleSaveSuccess = () => {
        if (!isResidence) return;

        setIsDetailModalOpen(false);
        setSelectedTalent(null);
        fetchTalents();
    };

    const handleSelectorClose = (action) => {
        if (action === 'back') {
            setIsSelectorOpen(true);
            return;
        }

        setIsSelectorOpen(false);
        setShowBackButton(false);
        setIsHome(true);
    };

    const handleBackToList = () => {
        setIsDetailModalOpen(false);
        setSelectedTalent(null);

        if (isResidence) {
            setResidenceView('list');
            return;
        }

        setIsSelectorOpen(true);
    };

    const handleOpenResidenceList = () => {
        if (residenceView === 'list') {
            fetchTalents();
            return;
        }

        setResidenceView('list');
    };

    const welcomeTitle = isResidence ? '在留管理' : t('accountingManagement');
    const buttonText = isResidence ? '人材の表示' : t('registerUpdateLoan');
    const shouldShowActions = isResidence ? residenceView !== 'home' : !isHome;
    const shouldShowHome = isResidence ? residenceView === 'home' : isHome;

    const handleBackToHome = () => {
        setIsSelectorOpen(false);
        setIsDetailModalOpen(false);
        setSelectedTalent(null);
        setShowBackButton(false);

        if (isResidence) {
            setResidenceView('home');
            return;
        }

        setIsHome(true);
    };

    const actions = shouldShowActions ? (
        <div className="flex gap-2">
            <button
                onClick={handleBackToHome}
                className="px-4 py-2 text-sm font-semibold rounded-lg shadow-md bg-purple-600 text-white hover:bg-purple-700 flex items-center"
            >
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"></path>
                </svg>
                {t('backToHome')}
            </button>
            <button
                onClick={() => {
                    if (isResidence) {
                        handleOpenResidenceList();
                    } else {
                        setIsSelectorOpen(true);
                        setShowBackButton(false);
                        setIsHome(false);
                    }
                }}
                className="px-4 py-2 text-sm font-semibold rounded-lg shadow-md bg-green-600 text-white hover:bg-green-700"
            >
                {buttonText}
            </button>
        </div>
    ) : null;

    const renderDetailModal = () => {
        if (!selectedTalent) return null;

        if (isResidence) {
            return (
                <ResidenceInfoModal
                    isOpen={isDetailModalOpen}
                    onClose={handleCloseDetailModal}
                    onSaveSuccess={handleSaveSuccess}
                    talent={selectedTalent}
                    showBackButton={false}
                    onBack={handleBackToList}
                />
            );
        }

        if (isAccounting) {
            return (
                <LoanInfoModal
                    isOpen={isDetailModalOpen}
                    onClose={handleCloseDetailModal}
                    talent={selectedTalent}
                    showBackButton={showBackButton}
                    onBack={handleBackToList}
                />
            );
        }

        return null;
    };

    return (
        <>
            <TalentSelectorModal
                isOpen={isSelectorOpen && isAccounting}
                onClose={handleSelectorClose}
                onSelect={handleTalentSelected}
            />
            {renderDetailModal()}
            <Layout title={welcomeTitle} actions={actions} fullWidth={isResidence && (residenceView === 'list' || residenceView === 'news')}>
                {shouldShowHome && (
                    <div className="space-y-8">
                        <div className="section-header">
                            <div className="section-header-icon">
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <div>
                                <h2 className="section-header-title">クイックアクション</h2>
                                <p className="section-header-subtitle">よく使う機能にすばやくアクセス</p>
                            </div>
                        </div>

                        <div className="dashboard-bento-grid">
                            <button
                                onClick={() => {
                                    if (isResidence) {
                                        handleOpenResidenceList();
                                    } else {
                                        setIsSelectorOpen(true);
                                        setShowBackButton(false);
                                        setIsHome(false);
                                    }
                                }}
                                className={`action-card ${isResidence ? 'action-card--blue' : 'action-card--green'}`}
                            >
                                <div className="action-card-icon">
                                    <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        {isResidence ? (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                        ) : (
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        )}
                                    </svg>
                                </div>
                                <span className="action-card-title">{buttonText}</span>
                                <span className="action-card-subtitle">
                                    {isResidence ? '人材の在留情報を管理' : '貸付金を登録・更新'}
                                </span>
                            </button>
                            <NewsActionCard
                                onClick={() => {
                                    if (isResidence) {
                                        setResidenceView('news');
                                        return;
                                    }

                                    setIsHome(false);
                                }}
                            />
                        </div>

                        <SystemNotificationPanel />

                        {isAccounting && <AccountingAnalytics />}

                        <div className="welcome-section text-center">
                            <h2 className="text-xl font-semibold text-gray-800 mb-2">ようこそ, {user.username}!</h2>
                            <p className="text-base text-[var(--text-secondary)] mb-4">上のボタンをクリックして作業を開始してください。</p>
                            <div className="pt-4 border-t border-[var(--border)]">
                                <p className="text-sm text-[var(--text-muted)] mb-4">「あれ？」っと思ったら、とりあえずリフレッシュ！まだ「あれ？」が続くなら、再ログインしてみてください。万策尽きたら、こちらへどうぞ！</p>
                                <a
                                    href="https://www.larksuite.com/invitation/page/add_contact/?token=93fn2903-69a2-4e4b-a8be-2d8b49qvicbv"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="support-link"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                    </svg>
                                    サポートへ連絡
                                </a>
                            </div>
                        </div>
                    </div>
                )}

                {isResidence && residenceView === 'list' && (
                    <div className="flex h-screen">
                        <FilterPanel
                            role={user.role}
                            filters={filters}
                            onFiltersChange={setFilters}
                        />

                        <div className="flex-1 overflow-auto">
                            <div className="mb-4 bg-[var(--surface)] p-4 rounded-lg shadow-[var(--shadow-sm)] border border-[var(--border)]">
                                <div className="text-sm text-[var(--text-secondary)] mb-2">
                                    {t('totalRecords')}: {totalCount}件 | {t('page')} {currentPage} / {totalPages}
                                </div>
                                <input
                                    type="text"
                                    placeholder={t('searchNameNumber')}
                                    className="w-full px-4 py-2 border border-[var(--border)] rounded-lg focus:ring-2 focus:ring-[var(--primary-200)] focus:border-[var(--primary-500)] transition-all duration-200"
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>

                            <TalentTable
                                talents={talents}
                                isLoading={isLoading}
                                feeTotals={feeTotals}
                                userInfo={user}
                                onDataRefresh={fetchTalents}
                                onViewEdit={handleTalentSelected}
                                sortBy={sortBy}
                                setSortBy={setSortBy}
                                sortDirection={sortDirection}
                                setSortDirection={setSortDirection}
                            />

                            {totalPages > 1 && (
                                <div className="mt-4 bg-white p-4 rounded-lg shadow">
                                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                                        <div className="text-sm text-gray-600">
                                            {t('showingResults', {
                                                start: (currentPage - 1) * pageSize + 1,
                                                end: Math.min(currentPage * pageSize, totalCount),
                                                total: totalCount
                                            })}
                                        </div>

                                        <div className="flex items-center space-x-2">
                                            <button
                                                onClick={() => setCurrentPage(1)}
                                                disabled={currentPage === 1}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('firstPage')}
                                            </button>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                                disabled={currentPage === 1}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('previousPage')}
                                            </button>

                                            <span className="px-4 py-2 text-sm text-gray-700">
                                                {t('pageInfo', { current: currentPage, total: totalPages })}
                                            </span>

                                            <button
                                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('nextPage')}
                                            </button>

                                            <button
                                                onClick={() => setCurrentPage(totalPages)}
                                                disabled={currentPage === totalPages}
                                                className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {t('lastPage')}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {isResidence && residenceView === 'news' && <SpecifiedSkillNewsPanel />}
            </Layout>
        </>
    );
}
